
// Единая логика ролей гранитных поверхностей для FRONT.
// Совместима с surfaceFinishPolicy, который сохраняется из ADMIN.

export const SURFACE_FINISH = Object.freeze({
    AUTO: 'auto',
    UNKNOWN: 'unknown',
    UNPOLISHED: 'unpolished',
    POLISHED: 'polished',
})

export const SURFACE_POLICY_MODE = Object.freeze({
    MODEL: 'model',
    ALL_POLISHED: 'allPolished',
})

const compactSpaces = (value = '') => String(value || '')
    .replace(/[._-]+/g, ' ')
    .replace(/[()[\]{}]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const stripBlenderSuffix = (value = '') => compactSpaces(value)
    .replace(/(?:\s|^)(?:copy|копия)\s*\d*$/iu, '')
    .replace(/\s+\d{1,4}$/u, '')
    .trim()

export const normalizeGraniteMaterialKey = (value = '') => {
    const key = String(value || '').trim().toLowerCase()
    return key === 'kapustinsky' ? 'vinga' : key
}

export const normalizeGltfMaterialName = (value = '') => stripBlenderSuffix(
    String(value || '')
        .trim()
        .toLowerCase()
        .replace(/ё/g, 'е')
)

export const getOriginalGltfMaterialName = (material, fallbackIndex = 0) => (
    String(material?.name || '').trim() || `Материал GLB ${fallbackIndex + 1}`
)

export const detectSurfaceFinishRole = (materialOrName, fallbackIndex = 0) => {
    const material = materialOrName && typeof materialOrName === 'object'
        ? materialOrName
        : null
    const originalName = material
        ? getOriginalGltfMaterialName(material, fallbackIndex)
        : String(materialOrName || '')
    const normalizedName = normalizeGltfMaterialName(originalName)

    const hasUnpolishedMarker = (
        /(?:^|\s)не\s*полир/u.test(normalizedName)
        || /(?:^|\s)неполир/u.test(normalizedName)
        || /(?:^|\s)матов/u.test(normalizedName)
        || /(?:^|\s)шлифован/u.test(normalizedName)
        || /(?:^|\s)unpolish/u.test(normalizedName)
        || /(?:^|\s)unpolished/u.test(normalizedName)
        || /(?:^|\s)matte/u.test(normalizedName)
        || /(?:^|\s)rough/u.test(normalizedName)
        || /(?:^|\s)raw\s*(?:stone|granite)?/u.test(normalizedName)
    )

    if (hasUnpolishedMarker) return SURFACE_FINISH.UNPOLISHED

    const hasPolishedMarker = (
        /(?:^|\s)полир/u.test(normalizedName)
        || /(?:^|\s)глян/u.test(normalizedName)
        || /(?:^|\s)polish/u.test(normalizedName)
        || /(?:^|\s)polished/u.test(normalizedName)
        || /(?:^|\s)gloss/u.test(normalizedName)
    )

    if (hasPolishedMarker) return SURFACE_FINISH.POLISHED

    const roughness = Number(material?.roughness)

    if (Number.isFinite(roughness)) {
        if (roughness >= 0.62) return SURFACE_FINISH.UNPOLISHED
        if (roughness <= 0.35) return SURFACE_FINISH.POLISHED
    }

    return SURFACE_FINISH.UNKNOWN
}

const normalizeSlotOverride = (value) => {
    const materialIndex = Math.max(0, Math.floor(Number(value?.materialIndex) || 0))
    const finish = [SURFACE_FINISH.UNPOLISHED, SURFACE_FINISH.POLISHED].includes(value?.finish)
        ? value.finish
        : SURFACE_FINISH.AUTO

    if (finish === SURFACE_FINISH.AUTO) return null

    return { materialIndex, finish }
}

export const normalizeSurfaceFinishPolicy = (value = {}) => {
    const allowPolishOverride = value?.allowPolishOverride === true
    const requestedMode = value?.mode === SURFACE_POLICY_MODE.ALL_POLISHED
        ? SURFACE_POLICY_MODE.ALL_POLISHED
        : SURFACE_POLICY_MODE.MODEL
    const slotOverrides = []
    const seen = new Set()

    ;(Array.isArray(value?.slotOverrides) ? value.slotOverrides : []).forEach((item) => {
        const normalized = normalizeSlotOverride(item)

        if (!normalized || seen.has(normalized.materialIndex)) return

        seen.add(normalized.materialIndex)
        slotOverrides.push(normalized)
    })

    return {
        autoDetect: value?.autoDetect !== false,
        allowPolishOverride,
        mode: allowPolishOverride ? requestedMode : SURFACE_POLICY_MODE.MODEL,
        slotOverrides: allowPolishOverride ? slotOverrides : [],
    }
}

export const getEffectiveSurfaceFinish = ({
    material,
    materialIndex = 0,
    policy = {},
}) => {
    const normalized = normalizeSurfaceFinishPolicy(policy)
    const detected = normalized.autoDetect === false
        ? SURFACE_FINISH.UNKNOWN
        : detectSurfaceFinishRole(material, materialIndex)

    if (!normalized.allowPolishOverride) return detected

    const explicit = normalized.slotOverrides.find((item) => (
        item.materialIndex === Math.max(0, Math.floor(Number(materialIndex) || 0))
    ))

    if (explicit?.finish) return explicit.finish
    if (normalized.mode === SURFACE_POLICY_MODE.ALL_POLISHED) return SURFACE_FINISH.POLISHED

    return detected
}

const getCatalogSurfaceHaystack = (catalog = {}) => normalizeGltfMaterialName([
    catalog?.key,
    catalog?.name,
    ...(Array.isArray(catalog?.aliases) ? catalog.aliases : []),
].filter(Boolean).join(' '))

export const isUnpolishedSurfaceCatalog = (catalog = {}) => {
    const haystack = getCatalogSurfaceHaystack(catalog)

    return (
        /(?:^|\s)не\s*полир/u.test(haystack)
        || /(?:^|\s)неполир/u.test(haystack)
        || /(?:^|\s)матовая?\s+поверхность/u.test(haystack)
        || /(?:^|\s)unpolished(?:\s+surface)?/u.test(haystack)
        || /(?:^|\s)rough(?:\s+surface)?/u.test(haystack)
    )
}

export const getUnpolishedSurfaceCatalog = (catalogs = []) => (
    (Array.isArray(catalogs) ? catalogs : []).find((catalog) => (
        catalog?.enabled !== false && isUnpolishedSurfaceCatalog(catalog)
    )) || null
)

export const isSurfacePseudoCatalog = (catalog = {}) => {
    const haystack = getCatalogSurfaceHaystack(catalog)

    return (
        isUnpolishedSurfaceCatalog(catalog)
        || /(?:^|\s)полированная\s+поверхность/u.test(haystack)
        || /(?:^|\s)polished(?:\s+surface)?/u.test(haystack)
        || /(?:^|\s)surface\s*finish/u.test(haystack)
    )
}

const cloneTexturePreset = (preset, {
    materialKey = '',
    materialName = '',
    keepIdentity = true,
} = {}) => {
    if (!preset || typeof preset !== 'object' || !preset.url) return null

    return {
        ...preset,
        repeat: Array.isArray(preset.repeat) ? preset.repeat.slice(0, 2) : [1, 1],
        offset: Array.isArray(preset.offset) ? preset.offset.slice(0, 2) : [0, 0],
        materialKey: keepIdentity ? normalizeGraniteMaterialKey(materialKey || preset.materialKey) : '',
        materialName: materialName || preset.materialName || '',
    }
}

export const createGraniteSceneSelection = ({
    materialCatalog,
    unpolishedSurfaceCatalog = null,
    surfaceFinishPolicy = {},
}) => {
    const materialKey = normalizeGraniteMaterialKey(materialCatalog?.key)
    const materialName = String(materialCatalog?.name || materialKey || 'Гранит')

    return {
        materialKey,
        materialName,
        polishedTexture: cloneTexturePreset(materialCatalog?.texturePreset, {
            materialKey,
            materialName,
            keepIdentity: true,
        }),
        // Важно: у фиксированной неполированной текстуры materialKey очищаем.
        // Иначе расчёт цены мог бы принять псевдо-материал поверхности за выбранную породу.
        unpolishedTexture: cloneTexturePreset(unpolishedSurfaceCatalog?.texturePreset, {
            materialKey: '',
            materialName: String(unpolishedSurfaceCatalog?.name || 'Неполированная поверхность'),
            keepIdentity: false,
        }),
        surfaceFinishPolicy: normalizeSurfaceFinishPolicy(surfaceFinishPolicy),
    }
}

export const getGraniteSceneSelection = (appearance) => {
    if (!appearance || typeof appearance !== 'object' || Array.isArray(appearance)) return null

    const granite = appearance.__granite

    if (!granite || typeof granite !== 'object' || Array.isArray(granite)) return null

    return granite
}

/**
 * Канонический источник визуальных параметров гранита — текущий каталог
 * /public/blank-materials. В scene snapshot хранится materialKey и запасная
 * копия preset для совместимости со старыми/офлайн сценами, но при наличии
 * каталога FRONT всегда берёт актуальные repeat/offset/rotation/roughness/
 * metalness из того же texturePreset, который редактируется в ADMIN.
 */
export const resolveGraniteAppearanceFromCatalog = (appearance, catalogs = []) => {
    const granite = getGraniteSceneSelection(appearance)

    if (!granite) return appearance

    const list = Array.isArray(catalogs) ? catalogs : []
    const materialKey = normalizeGraniteMaterialKey(granite.materialKey)
    const materialCatalog = list.find((catalog) => (
        catalog?.enabled !== false
        && normalizeGraniteMaterialKey(catalog?.key) === materialKey
        && !isSurfacePseudoCatalog(catalog)
    )) || null
    const unpolishedCatalog = getUnpolishedSurfaceCatalog(list)

    if (!materialCatalog && !unpolishedCatalog) return appearance

    const materialName = String(
        materialCatalog?.name
        || granite.materialName
        || materialKey
        || 'Гранит'
    )

    const polishedTexture = cloneTexturePreset(
        materialCatalog?.texturePreset || granite.polishedTexture,
        {
            materialKey,
            materialName,
            keepIdentity: true,
        }
    ) || granite.polishedTexture || null

    const unpolishedTexture = cloneTexturePreset(
        unpolishedCatalog?.texturePreset || granite.unpolishedTexture,
        {
            materialKey: '',
            materialName: String(
                unpolishedCatalog?.name
                || granite.unpolishedTexture?.materialName
                || 'Неполированная поверхность'
            ),
            keepIdentity: false,
        }
    ) || granite.unpolishedTexture || null

    return {
        ...appearance,
        __granite: {
            ...granite,
            materialKey,
            materialName,
            polishedTexture,
            unpolishedTexture,
        },
    }
}

export const getGraniteMaterialKeyFromAppearance = (appearance) => {
    const granite = getGraniteSceneSelection(appearance)

    if (granite?.materialKey) return normalizeGraniteMaterialKey(granite.materialKey)

    const walk = (value, depth = 0) => {
        if (depth > 7 || value == null) return ''

        if (Array.isArray(value)) {
            for (const item of value) {
                const hit = walk(item, depth + 1)
                if (hit) return hit
            }
            return ''
        }

        if (typeof value !== 'object') return ''

        const direct = normalizeGraniteMaterialKey(value.materialKey || value.blankMaterialKey)
        if (direct) return direct

        for (const nested of Object.values(value)) {
            const hit = walk(nested, depth + 1)
            if (hit) return hit
        }

        return ''
    }

    return walk(appearance)
}

export const resolveGraniteAppearanceValue = (
    appearance,
    material,
    materialIndex = 0,
    materialCount = 1
) => {
    const granite = getGraniteSceneSelection(appearance)

    if (!granite) return undefined

    let finish = getEffectiveSurfaceFinish({
        material,
        materialIndex,
        policy: granite.surfaceFinishPolicy || {},
    })

    // Одноматериальные старые модели безопасно считаем полированными:
    // там физически нет отдельного слота неполированной поверхности.
    if (finish === SURFACE_FINISH.UNKNOWN && Number(materialCount) === 1) {
        finish = SURFACE_FINISH.POLISHED
    }

    if (finish === SURFACE_FINISH.UNPOLISHED) {
        return granite.unpolishedTexture || 'original'
    }

    if (finish === SURFACE_FINISH.POLISHED) {
        return granite.polishedTexture || 'original'
    }

    // UNKNOWN не перетираем: пусть старые index-based цвета продолжают работать
    // для металла/декора в смешанных моделях.
    return undefined
}
